/**
 * Sprint 0.20 — Frontend-Upload-Wizard für die Multi-Agent-Pipeline.
 *
 * 3 Schritte:
 *   1. Datei wählen (DropZone)
 *   2. Vor-Analyse (PageReportCard, Browser-Klassifikation)
 *   3. Run-Status (Polling, Auto-Redirect zur Run-Page)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DropZone from "../components/DropZone";
import Stepper from "../components/Stepper";
import PageReportCard from "../components/PageReportCard";
import { api } from "../api";
import { classifyPdfInBrowser } from "../lib/pdfClassify";
import type { PreUploadReport } from "@bidi-takeoff/shared/pdf-classify-page";
import type { PlanTyp } from "@bidi-takeoff/shared/gewerk-catalog";

// Sprint A.3 — Plan-Typ-Pflicht beim Upload. Reihenfolge im Picker:
// MVP-supported zuerst, dann andere als "klassifiziert aber Pipeline läuft
// nicht (Sprint H)" markiert.
//
// Sprint P (2026-05-03): `ansicht-fassade` ist jetzt MVP — Verblender-
// Stempel-Fang via Vision liest die m²-Box pro Ansicht und schreibt sie
// nach 00-plan-annotations.json#ansichtData.
const PLAN_TYPE_OPTIONS: Array<{
  value: PlanTyp;
  label: string;
  short: string;
  mvp: boolean;
}> = [
  { value: "grundriss", label: "Grundriss", short: "GR", mvp: true },
  { value: "schnitt", label: "Schnitt", short: "SC", mvp: true },
  { value: "ansicht-fassade", label: "Ansicht / Fassade", short: "AN", mvp: true },
  { value: "detail", label: "Detail", short: "DT", mvp: false },
  { value: "lageplan", label: "Lageplan", short: "LP", mvp: false },
  { value: "elektroplan", label: "Elektro", short: "EL", mvp: false },
  { value: "sanitaerplan", label: "Sanitär", short: "SA", mvp: false },
  { value: "heizungsplan", label: "Heizung", short: "HZ", mvp: false },
  { value: "lueftungsplan", label: "Lüftung", short: "LU", mvp: false },
  { value: "brandschutzplan", label: "Brandschutz", short: "BS", mvp: false },
  { value: "statik-bewehrung", label: "Statik / Bewehrung", short: "ST", mvp: false },
];

type WizardStep = 0 | 1 | 2;

const STEPS = ["Datei wählen", "Vor-Analyse", "Run-Status"];
const POLL_INTERVAL_MS = 1500;

export default function MultiagentUploadPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<WizardStep>(0);

  // Schritt 1: gewählte Datei
  const [file, setFile] = useState<File | null>(null);
  // Schritt 2: Vor-Analyse
  const [report, setReport] = useState<PreUploadReport | null>(null);
  const [classifyError, setClassifyError] = useState<string | null>(null);
  const [classifying, setClassifying] = useState(false);
  // Sprint A.3 — Plan-Typ-Pflicht. Default Grundriss; User muss in Schritt 2
  // bestätigen oder ändern bevor "Run starten" aktiv wird.
  const [planTyp, setPlanTyp] = useState<PlanTyp>("grundriss");
  // Schritt 3: laufender Run
  const [runId, setRunId] = useState<string | null>(null);
  const [runStatus, setRunStatus] = useState<{
    phase: string;
    completedPhases: string[];
    totalPhases: number;
    error: string | null;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // StrictMode-safe: ref muss beim (Re-)Mount auf false zurückgesetzt werden,
  // sonst blockiert die Cleanup-Funktion vom ersten Mount alle weiteren
  // async-Completions.
  const cancelled = useRef(false);
  useEffect(() => {
    cancelled.current = false;
    return () => {
      cancelled.current = true;
    };
  }, []);

  const onFile = useCallback(async (f: File) => {
    setFile(f);
    setClassifyError(null);
    setClassifying(true);
    setStep(1);
    try {
      const r = await classifyPdfInBrowser(f);
      if (!cancelled.current) setReport(r);
    } catch (err) {
      if (!cancelled.current) {
        setClassifyError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      if (!cancelled.current) setClassifying(false);
    }
  }, []);

  const startRun = useCallback(async () => {
    if (!file) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { runId: newId } = await api.multiagentStartRun(file, {
        mode: "auto",
        planTyp,
      });
      if (cancelled.current) return;
      setRunId(newId);
      setStep(2);
    } catch (err) {
      if (!cancelled.current) {
        setSubmitError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      if (!cancelled.current) setSubmitting(false);
    }
  }, [file, planTyp]);

  // Polling während Schritt 3
  useEffect(() => {
    if (step !== 2 || !runId) return;
    let stopped = false;
    const tick = async () => {
      try {
        const s = await api.multiagentRunStatus(runId);
        if (stopped || cancelled.current) return;
        setRunStatus(s);
        if (s.phase === "complete") {
          // Auto-Redirect zur Detail-Page
          setTimeout(() => {
            if (!cancelled.current) navigate(`/multiagent/${runId}`);
          }, 600);
          return;
        }
        if (s.phase === "error") return; // bleibt sichtbar mit Fehlermeldung
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (err) {
        if (!stopped && !cancelled.current) {
          setRunStatus((prev) => ({
            phase: "error",
            completedPhases: prev?.completedPhases ?? [],
            totalPhases: prev?.totalPhases ?? 5,
            error: err instanceof Error ? err.message : String(err),
          }));
        }
      }
    };
    void tick();
    return () => {
      stopped = true;
    };
  }, [step, runId, navigate]);

  const reset = useCallback(() => {
    setFile(null);
    setReport(null);
    setClassifyError(null);
    setRunId(null);
    setRunStatus(null);
    setSubmitError(null);
    setStep(0);
  }, []);

  const showRasterWarning = useMemo(
    () => !!report && report.hasAnyRasterPage,
    [report]
  );

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold">Neuer Multi-Agent-Run</h1>
        <p className="text-sm text-slate-500 mt-1">
          Plan-PDF hochladen, Vor-Analyse prüfen, Pipeline starten.
        </p>
      </div>

      <div className="mb-6 px-1">
        <Stepper steps={STEPS} currentIndex={step} />
      </div>

      {step === 0 && <Step1 onFile={onFile} />}

      {step === 1 && (
        <Step2
          file={file}
          report={report}
          classifying={classifying}
          classifyError={classifyError}
          submitting={submitting}
          submitError={submitError}
          showRasterWarning={showRasterWarning}
          planTyp={planTyp}
          onPlanTypChange={setPlanTyp}
          onReset={reset}
          onStart={startRun}
        />
      )}

      {step === 2 && (
        <Step3
          runId={runId}
          status={runStatus}
          onReset={reset}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Schritt 1: Datei wählen
// ----------------------------------------------------------------------------

function Step1({ onFile }: { onFile: (f: File) => void }) {
  return (
    <div className="space-y-3">
      <DropZone onFile={onFile} maxSizeMB={100} />
      <p className="text-xs text-slate-500 px-1">
        Die Vor-Analyse läuft direkt im Browser — keine Wartezeit, kein Upload
        bevor du das Ergebnis siehst.
      </p>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Schritt 2: Vor-Analyse
// ----------------------------------------------------------------------------

interface Step2Props {
  file: File | null;
  report: PreUploadReport | null;
  classifying: boolean;
  classifyError: string | null;
  submitting: boolean;
  submitError: string | null;
  showRasterWarning: boolean;
  planTyp: PlanTyp;
  onPlanTypChange: (p: PlanTyp) => void;
  onReset: () => void;
  onStart: () => void;
}

function Step2({
  file,
  report,
  classifying,
  classifyError,
  submitting,
  submitError,
  showRasterWarning,
  planTyp,
  onPlanTypChange,
  onReset,
  onStart,
}: Step2Props) {
  const fileSizeMB = file ? (file.size / (1024 * 1024)).toFixed(1) : "?";

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{file?.name ?? "—"}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {fileSizeMB} MB · {report?.numPages ?? "?"} Seite
            {(report?.numPages ?? 0) === 1 ? "" : "n"}
          </div>
        </div>
        <button
          onClick={onReset}
          className="text-xs text-slate-600 hover:text-slate-900 underline"
        >
          Andere Datei
        </button>
      </div>

      {classifying && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Spinner /> Browser klassifiziert die Seiten…
          </div>
        </div>
      )}

      {classifyError && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
          Klassifikation fehlgeschlagen: {classifyError}
        </div>
      )}

      {/* Sprint A.3 — Plan-Typ-Picker, Pflicht-Feld vor Run-Start. */}
      {report && (
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <div className="text-sm font-medium text-slate-700 mb-1">
            Plan-Typ
            <span className="ml-2 text-[11px] font-normal text-slate-500">
              bestimmt welche Pipeline läuft
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {PLAN_TYPE_OPTIONS.map((opt) => {
              const active = planTyp === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => onPlanTypChange(opt.value)}
                  className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                    active
                      ? "bg-brand-600 text-white border-brand-600"
                      : opt.mvp
                        ? "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                        : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                  }`}
                  title={
                    opt.mvp
                      ? "Voll unterstützt — Polygon-/Mengen-Pipeline läuft"
                      : "Wird abgelegt, geometrische Pipeline läuft im MVP nicht (Sprint H)"
                  }
                >
                  {opt.label}
                  {!opt.mvp && (
                    <span className="ml-1 text-[10px] opacity-70">⏳</span>
                  )}
                </button>
              );
            })}
          </div>
          {!PLAN_TYPE_OPTIONS.find((o) => o.value === planTyp)?.mvp && (
            <p className="text-[11px] text-amber-700 mt-2 leading-relaxed">
              ⚠ Plan-Typ wird klassifiziert und im Run abgelegt — die Polygon-
              und Mengen-Pipeline läuft im MVP nur bei Grundriss, Schnitt und
              Ansicht/Fassade. Sprint H erweitert das.
            </p>
          )}
        </div>
      )}

      {report && (
        <>
          {showRasterWarning && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
              <div className="font-medium mb-1">
                ⚠ Manuelle Vorbereitung erforderlich
              </div>
              <div className="text-amber-800">
                Mindestens eine Seite ist Raster oder Mischform. Du musst nach
                dem Run wahrscheinlich Maßstab setzen, Wände manuell zeichnen
                und Materialien wählen. Vektor-PDFs mit OCG-Layer laufen
                vollautomatisch durch — bei Raster muss der Mensch ran.
              </div>
            </div>
          )}

          <div className="space-y-3">
            {report.pages.map((p) => (
              <PageReportCard
                key={p.index}
                page={p}
                detectedScaleHint={report.detectedScaleHint}
              />
            ))}
          </div>

          {submitError && (
            <div role="alert" className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
              Run-Start fehlgeschlagen: {submitError}
            </div>
          )}

          <div className="flex justify-between gap-3 pt-2">
            <button
              onClick={onReset}
              disabled={submitting}
              className="px-4 py-2 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              onClick={onStart}
              disabled={submitting}
              className="px-5 py-2 text-sm rounded-md bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-wait inline-flex items-center gap-2"
            >
              {submitting ? <><Spinner light /> Starte…</> : "Run starten"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Schritt 3: Run-Status
// ----------------------------------------------------------------------------

interface Step3Props {
  runId: string | null;
  status: {
    phase: string;
    completedPhases: string[];
    totalPhases: number;
    error: string | null;
  } | null;
  onReset: () => void;
}

const PHASE_LABELS: Record<string, string> = {
  annotations: "Plan-Typ erkannt",
  classification: "PDF klassifiziert",
  primitives: "Linien & Texte extrahiert",
  polygons: "Räume gebaut",
  dimensions: "Maßstab + Bemaßungen",
  rooms: "DIN 277 + Gewerke",
};

function Step3({ runId, status, onReset }: Step3Props) {
  const completed = status?.completedPhases.length ?? 0;
  const total = status?.totalPhases ?? 5;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isError = status?.phase === "error";
  const isComplete = status?.phase === "complete";

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">
          Run-ID
        </div>
        <div className="font-mono text-sm break-all">{runId ?? "—"}</div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">
            {isError
              ? "Fehler"
              : isComplete
                ? "Pipeline fertig — leite weiter…"
                : `Phase ${completed + 1} von ${total}`}
          </div>
          <div className="text-xs font-mono text-slate-500">{pct}%</div>
        </div>

        <div
          className="h-2 bg-slate-100 rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={`h-full transition-all duration-300 ${
              isError ? "bg-red-500" : "bg-brand-500"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>

        <ol className="space-y-1 text-sm">
          {Object.entries(PHASE_LABELS).map(([key, label]) => {
            const done = status?.completedPhases.includes(key);
            return (
              <li key={key} className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className={done ? "text-emerald-600" : "text-slate-300"}
                >
                  {done ? "✓" : "○"}
                </span>
                <span className={done ? "text-slate-700" : "text-slate-500"}>
                  {label}
                </span>
              </li>
            );
          })}
        </ol>
      </div>

      {isError && status?.error && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
          <div className="font-medium mb-1">Pipeline-Fehler:</div>
          <div className="font-mono text-xs whitespace-pre-wrap">
            {status.error}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        {isError && (
          <button
            onClick={onReset}
            className="px-4 py-2 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Neue Datei
          </button>
        )}
        {!isError && !isComplete && runId && (
          <a
            href={`/multiagent/${runId}`}
            className="px-4 py-2 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Im Hintergrund laufen lassen → Run öffnen
          </a>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------

function Spinner({ light = false }: { light?: boolean }) {
  return (
    <svg
      className={`animate-spin h-3.5 w-3.5 ${light ? "text-white" : "text-slate-500"}`}
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  );
}
